import i18next from "i18next";
import { createSelector } from "reselect";
import { getEntity } from "utils/entities";
import { getStoreEntity } from "services/store";
import { formatTags, presentLayer } from "utils/presenters";
import {
  TemplateSchema,
  ClusterProfileSchema,
  PolicySchema,
  ClusterProfileTemplateSchema,
  VariableSchema,
} from "utils/schemas";
import { maintenancePolicyDetailsFetcher } from "../services/templates";

export const getRawTemplate = getEntity(
  (state) => state.templates?.details.currentTemplateGuid,
  TemplateSchema
);

export const getAssociatedPolicies = createSelector(
  (state) => getRawTemplate(state),
  (template) => {
    return template?.spec?.policies || [];
  }
);

const getAssociatedProfileEntities = createSelector(
  getRawTemplate,
  (template) => {
    const associatedProfilesGuids = (template?.spec?.profiles || []).map(
      ({ guid }) => guid
    );

    return (
      getStoreEntity(associatedProfilesGuids, [ClusterProfileTemplateSchema]) ||
      []
    );
  }
);

export const getAssociatedProfileDetails = createSelector(
  getAssociatedProfileEntities,
  (profiles = []) => {
    return profiles.map((profile) => ({
      ...profile,
      profileVersion: profile.spec?.version,
      name: profile.metadata.name,
      uid: profile.metadata.uid,
      packs: profile.packs || [],
    }));
  }
);

//TODO this selector has to be modified once common modules are introduced
const getSelectedProfileEntities = createSelector(
  (state) => state.templates?.configuration.isEditTemplate,
  (state) => state.forms.template?.data?.clusterProfiles,
  (state) => state.forms.templateProfiles?.data?.clusterProfiles || [],
  getAssociatedProfileDetails,
  (
    isEditTemplate,
    templateProfiles,
    editTemplateProfiles,
    associatedProfileDetails
  ) => {
    if (isEditTemplate) {
      const existingProfiles = editTemplateProfiles.map((guid) => {
        let entity = getStoreEntity(guid, ClusterProfileSchema);
        if (!entity) {
          entity = getStoreEntity(guid, ClusterProfileTemplateSchema);
        }
        return (
          entity ||
          associatedProfileDetails.find((profile) => profile.guid === guid)
        );
      });
      return existingProfiles;
    } else {
      const profileGuids = templateProfiles || [];
      return getStoreEntity(profileGuids, [ClusterProfileSchema]) || [];
    }
  }
);

export const getSelectedProfilesDetails = createSelector(
  getSelectedProfileEntities,
  (rawProfiles = []) => {
    return rawProfiles.map((profile) => ({
      ...profile,
      ...profile.spec.published,
      name: profile.metadata.name,
      profileVersion: profile.spec.version,
    }));
  }
);

export const getSelectedMaintenancePolicyGuid = createSelector(
  (state) => state.templates?.configuration.selectedMaintenancePolicyGuid,
  (selectedMaintenancePolicyGuid = "") => {
    return selectedMaintenancePolicyGuid;
  }
);

export const getDraftMaintenancePolicyGuid = createSelector(
  (state) => state.templates?.configuration.draftMaintenancePolicyGuid,
  (draftMaintenancePolicyGuid = "") => {
    return draftMaintenancePolicyGuid;
  }
);

export const getEditTemplate = createSelector(
  (state) => state.templates?.configuration.isEditTemplate,
  (isEditTemplate) => isEditTemplate
);

const getRawMaintenancePolicy = getEntity(
  (state) => state.forms.template?.data.maintenancePolicyGuid,
  PolicySchema
);

export const getSelectedProfileGuids = createSelector(
  (state) => state.templates?.configuration.selectedProfileGuids,
  (selectedProfileGuids = []) => {
    return selectedProfileGuids;
  }
);

export const getSelectedProfiles = createSelector(
  getSelectedProfileEntities,
  (selectedProfileEntities) => {
    const rawProfiles = selectedProfileEntities;
    const profileTypePriority = ["cluster", "infra", "add-on"];
    return (rawProfiles || [])
      .map((profile) => ({
        ...profile,
        layers: (profile.spec?.published?.packs || []).map((pack) => {
          return presentLayer({
            ...pack,
            type:
              pack?.layer === "addon"
                ? pack?.addonType || pack?.type
                : pack?.layer,
          });
        }),
      }))
      .sort((profileA, profileB) => {
        const profileTypeA = profileA?.spec?.published?.type || "add-on";
        const profileTypeB = profileB?.spec?.published?.type || "add-on";
        return (
          profileTypePriority.indexOf(profileTypeB) -
          profileTypePriority.indexOf(profileTypeA)
        );
      });
  }
);

export const getAssociatedProfiles = createSelector(
  (state) => getRawTemplate(state),
  (template) => {
    return template?.spec?.profiles || [];
  }
);

export const getTemplatePayload = createSelector(
  (state) => state.forms.template?.data,
  getRawMaintenancePolicy,
  (formData, maintenancePolicy) => {
    const {
      name = "",
      description = "",
      tags = [],
      cloudType = "",
      clusterProfiles = [],
    } = formData;
    const rawProfile =
      getStoreEntity(clusterProfiles, [ClusterProfileSchema]) || [];
    const policies = [];
    if (maintenancePolicy?.metadata?.uid) {
      policies.push({
        uid: maintenancePolicy.metadata.uid,
        kind: "maintenance",
      });
    }

    return {
      metadata: {
        name,
        annotations: {
          description,
        },
        labels: formatTags(tags),
      },
      spec: {
        cloudType,
        policies,
        profiles: rawProfile.map((profile) => ({
          uid: profile?.metadata?.uid,
        })),
      },
    };
  }
);

export const getTemplateFormError = createSelector(
  (state) => state.forms?.template,
  (state) => state.language.language,
  (form) => {
    if (!form) {
      return null;
    }

    const { errors = [] } = form;
    const filteredErrors = errors.filter((error) => error.result);

    if (!filteredErrors.length) {
      return null;
    }

    let message = i18next.t(
      "Please resolve the errors on the page in order to continue"
    );

    const maintenancePolicyError = filteredErrors.find(
      (error) => error.field === "maintenancePolicyGuid"
    );
    if (maintenancePolicyError) {
      return maintenancePolicyError.result;
    }

    const clusterProfilesError = filteredErrors.find(
      (error) => error.field === "clusterProfiles"
    );
    if (clusterProfilesError) {
      return clusterProfilesError.result;
    }

    return message;
  }
);

export const getSelectedMaintenancePolicyDetails = createSelector(
  maintenancePolicyDetailsFetcher.selector,
  ({ result = {} }) => {
    return result;
  }
);

export const getClustersStatusSummary = createSelector(
  getRawTemplate,
  (template) => {
    const clusterStatuses = template?.status?.clusterStatusCounts?.clusters;
    if (
      !clusterStatuses ||
      Object.values(clusterStatuses).every((group) => !group?.length)
    ) {
      return null;
    }
    return Object.keys(clusterStatuses).reduce((accum, key) => {
      const count = clusterStatuses[key].length || 0;
      if (count) {
        accum[key] = count;
      }
      return accum;
    }, {});
  }
);

export const getVariablesEntities = getEntity(
  (state) => state.templates?.details.variablesGuids,
  [VariableSchema]
);
